import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Check, X, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";

const currencyFmt = (v, cur = "USD") => {
  if (v === null || v === undefined || v === "") return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(v));
  } catch { return `${v}`; }
};

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

// Total payable per RC = agreed_rate + sum(accessorials.amount).
const rcTotal = (r) => {
  const base = Number(r.agreed_rate || 0);
  const acc = (r.accessorials || []).reduce((s, a) => s + Number(a.amount || 0), 0);
  return base + acc;
};

const DETAIL_COLS = [
  { key: "carrier_name",  label: "Carrier",       w: 200 },
  { key: "mc_number",     label: "MC #",          w: 100 },
  { key: "job_number",    label: "Job #",         w: 110 },
  { key: "bol_number",    label: "BOL #",         w: 110 },
  { key: "pickup_date",   label: "Pickup",        w: 110 },
  { key: "delivery_date", label: "Delivery",      w: 110 },
  { key: "total",         label: "Total",         w: 120 },
  { key: "paid_at",       label: "Paid Date",     w: 110 },
  { key: "payment_ref",   label: "Payment Ref",   w: 140 },
];

export default function CarrierPayPage() {
  const [status, setStatus] = useState("unpaid");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  // For the summary, always load ALL RCs regardless of the tab filter.
  const [allRows, setAllRows] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [detailRes, allRes] = await Promise.all([
        api.get("/carrier-pay", { params: status === "all" ? {} : { status } }),
        api.get("/carrier-pay"),
      ]);
      setRows(detailRes.data || []);
      setAllRows(allRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Per-carrier summary — always computed from the full data set so tabs
  // don't hide unpaid carriers when viewing "Paid" and vice versa.
  const summary = useMemo(() => {
    const map = new Map();
    for (const r of allRows) {
      const key = r.carrier_id || r.carrier_name || "—";
      const cur = map.get(key) || {
        key,
        carrier_name: r.carrier_name || "—",
        mc_number: r.mc_number || "",
        loads: 0,
        paid: 0,
        unpaid: 0,
        currency: r.currency || "USD",
      };
      const t = rcTotal(r);
      cur.loads += 1;
      if (r.is_paid) cur.paid += t;
      else cur.unpaid += t;
      map.set(key, cur);
    }
    return Array.from(map.values())
      .map((s) => ({ ...s, total: s.paid + s.unpaid }))
      .sort((a, b) => b.total - a.total);
  }, [allRows]);

  const grand = useMemo(() => summary.reduce((acc, s) => ({
    loads: acc.loads + s.loads,
    paid: acc.paid + s.paid,
    unpaid: acc.unpaid + s.unpaid,
    total: acc.total + s.total,
  }), { loads: 0, paid: 0, unpaid: 0, total: 0 }), [summary]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const blob = [r.carrier_name, r.mc_number, r.job_number, r.bol_number, r.payment_ref]
        .filter(Boolean).join(" ").toLowerCase();
      return blob.includes(s);
    });
  }, [rows, q]);

  const markPaid = async (r) => {
    const total = rcTotal(r);
    const ref = window.prompt(`Payment reference for ${r.carrier_name} (${currencyFmt(total, r.currency)}):`, "");
    if (ref === null) return;
    setBusyId(r.id);
    try {
      await api.patch(`/carrier-pay/${r.id}`, { paid: true, payment_ref: ref.trim() });
      toast.success(`Marked paid — ${r.carrier_name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusyId(null); }
  };

  const undoPaid = async (r) => {
    if (!window.confirm(`Un-mark payment for ${r.carrier_name}?`)) return;
    setBusyId(r.id);
    try {
      await api.patch(`/carrier-pay/${r.id}`, { paid: false });
      toast.success("Reverted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusyId(null); }
  };

  const totalW = DETAIL_COLS.reduce((a, c) => a + c.w, 0) + 100;

  return (
    <div className="p-6 space-y-5">
      <div>
        <div className="mono-label">ACCOUNTS PAYABLE</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Carrier Pay</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Per-carrier totals up top. Individual rate confirmations below — click Mark Paid to log a payment.
        </p>
      </div>

      {/* ─────────────── PER-CARRIER SUMMARY ─────────────── */}
      <div className="border border-zinc-200 bg-white">
        <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
          <div className="mono-label !text-zinc-100">CARRIER TOTALS</div>
          <div className="flex items-center gap-6 text-[11px] font-mono">
            <span>Carriers: <span className="font-bold">{summary.length}</span></span>
            <span>Loads: <span className="font-bold">{grand.loads}</span></span>
            <span className="text-emerald-300">Paid: <span className="font-bold">{currencyFmt(grand.paid, "USD")}</span></span>
            <span className="text-rose-300">Unpaid: <span className="font-bold">{currencyFmt(grand.unpaid, "USD")}</span></span>
            <span>Total: <span className="font-bold">{currencyFmt(grand.total, "USD")}</span></span>
          </div>
        </div>
        <div className="apex-grid-wrap">
          <table className="apex-grid" style={{ width: "100%", minWidth: 820 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 240 }}>Carrier</th>
                <th style={{ minWidth: 100 }}>MC #</th>
                <th style={{ minWidth: 80 }} className="text-right">Loads</th>
                <th style={{ minWidth: 140 }} className="text-right">Paid</th>
                <th style={{ minWidth: 140 }} className="text-right">Unpaid</th>
                <th style={{ minWidth: 140 }} className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-6 text-zinc-500">
                  No rate confirmations issued yet.
                </td></tr>
              ) : summary.map((s) => (
                <tr key={s.key} data-testid={`cp-summary-${s.key}`}>
                  <td className="font-medium">{s.carrier_name}</td>
                  <td className="font-mono text-[11px]">{s.mc_number}</td>
                  <td className="text-right font-mono">{s.loads}</td>
                  <td className="text-right font-mono text-emerald-800">{currencyFmt(s.paid, s.currency)}</td>
                  <td className={`text-right font-mono ${s.unpaid > 0 ? "text-rose-700 font-semibold" : "text-zinc-500"}`}>
                    {currencyFmt(s.unpaid, s.currency)}
                  </td>
                  <td className="text-right font-mono font-bold">{currencyFmt(s.total, s.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─────────────── DETAIL TABLE (per RC) ─────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="sharp bg-zinc-100 p-0 h-auto border border-zinc-950">
            <TabsTrigger value="unpaid" className="sharp px-5 py-2 text-xs data-[state=active]:bg-rose-600 data-[state=active]:text-white" data-testid="cp-tab-unpaid">
              UNPAID
            </TabsTrigger>
            <TabsTrigger value="paid" className="sharp px-5 py-2 text-xs data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="cp-tab-paid">
              PAID
            </TabsTrigger>
            <TabsTrigger value="all" className="sharp px-5 py-2 text-xs data-[state=active]:bg-[#0056B3] data-[state=active]:text-white" data-testid="cp-tab-all">
              ALL
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search carrier, job #, BOL #, MC #, ref…"
            className="sharp pl-8 h-9"
            data-testid="cp-search"
          />
        </div>
      </div>

      <div className="apex-grid-wrap">
        <table className="apex-grid" style={{ width: totalW }}>
          <thead>
            <tr>
              <th style={{ width: 100, minWidth: 100 }}>Action</th>
              {DETAIL_COLS.map((c) => (
                <th key={c.key} style={{ width: c.w, minWidth: c.w }} className={c.key === "total" ? "text-right" : ""}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={DETAIL_COLS.length + 1} className="text-center py-6 text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={DETAIL_COLS.length + 1} className="text-center py-6 text-zinc-500">
                {q ? "No matches." : status === "unpaid" ? "🎉 No unpaid carriers." : "No records."}
              </td></tr>
            ) : filtered.map((r) => {
              const total = rcTotal(r);
              return (
                <tr key={r.id} data-testid={`cp-row-${r.id}`}>
                  <td className="text-center" style={{ width: 100, minWidth: 100 }}>
                    {r.is_paid ? (
                      <button
                        disabled={busyId === r.id}
                        onClick={() => undoPaid(r)}
                        className="inline-flex items-center text-[11px] text-zinc-600 hover:text-rose-700 disabled:opacity-50"
                        data-testid={`cp-undo-${r.id}`}
                      >
                        <X className="h-3 w-3 mr-1" /> Undo
                      </button>
                    ) : (
                      <button
                        disabled={busyId === r.id}
                        onClick={() => markPaid(r)}
                        className="inline-flex items-center text-[11px] text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                        data-testid={`cp-mark-paid-${r.id}`}
                      >
                        <DollarSign className="h-3 w-3 mr-0.5" /> Mark Paid
                      </button>
                    )}
                  </td>
                  <td>{r.carrier_name || "—"}</td>
                  <td className="font-mono">{r.mc_number || "—"}</td>
                  <td className="font-mono">{r.job_number || "—"}</td>
                  <td className="font-mono">{r.bol_number || "—"}</td>
                  <td className="font-mono">{fmtDate(r.pickup_date)}</td>
                  <td className="font-mono">{fmtDate(r.delivery_date)}</td>
                  <td className="font-mono text-right font-bold">{currencyFmt(total, r.currency || "USD")}</td>
                  <td className="font-mono">
                    {r.is_paid ? (
                      <span className="inline-flex items-center text-emerald-700"><Check className="h-3 w-3 mr-1" />{fmtDate(r.paid_at)}</span>
                    ) : ""}
                  </td>
                  <td className="font-mono text-zinc-600">{r.payment_ref || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
